import type { DesignLiveAppointmentUpdatedEventInput } from '@99designs/graph-utils/types';

// The backend uses 0 for "any designer."
export const ANY_DESIGNER_CALENDAR_ID = 0;

export interface AppointmentValue {
    time: string;
    timezone: string | undefined;
    calendarId: number | undefined;
    designerName: string | undefined;
    // Persists an explicit matching choice.
    matchWithPreviousDesigner: boolean | undefined;
}

/**
 * Parses a clientConfiguredValue for the appointmentTime field.
 *
 * Handles two formats:
 *   - Legacy: bare ISO string, e.g. '2023-08-14T00:00:00Z'
 *   - New:    JSON envelope, e.g. '{"time":"2026-07-02T10:00:00","timezone":"Europe/Kyiv"}'
 *
 * Always returns a non-throwing result. Malformed JSON is treated as a bare time string.
 */
export function parseAppointmentValue(raw: string | null | undefined): AppointmentValue {
    if (!raw) {
        return {
            time: '',
            timezone: undefined,
            calendarId: undefined,
            designerName: undefined,
            matchWithPreviousDesigner: undefined,
        };
    }

    try {
        const parsed = JSON.parse(raw);
        if (
            parsed !== null &&
            typeof parsed === 'object' &&
            typeof parsed.time === 'string' &&
            typeof parsed.timezone === 'string'
        ) {
            return {
                time: parsed.time,
                timezone: parsed.timezone,
                calendarId: typeof parsed.calendarId === 'number' ? parsed.calendarId : undefined,
                designerName:
                    typeof parsed.designerName === 'string' ? parsed.designerName : undefined,
                matchWithPreviousDesigner:
                    typeof parsed.matchWithPreviousDesigner === 'boolean'
                        ? parsed.matchWithPreviousDesigner
                        : undefined,
            };
        }
    } catch (_) {
        // not JSON — fall through to bare string treatment
    }

    return {
        time: raw,
        timezone: undefined,
        calendarId: undefined,
        designerName: undefined,
        matchWithPreviousDesigner: undefined,
    };
}

/** Serializes an appointment and optional designer. */
export function serializeAppointmentValue(
    time: string,
    timezone: string,
    calendarId?: number,
    designerName?: string,
    matchWithPreviousDesigner?: boolean
): string {
    const hasSpecificDesigner = calendarId !== undefined && calendarId !== ANY_DESIGNER_CALENDAR_ID;
    return JSON.stringify({
        time,
        timezone,
        ...(hasSpecificDesigner ? { calendarId } : {}),
        ...(hasSpecificDesigner && designerName ? { designerName } : {}),
        ...(matchWithPreviousDesigner !== undefined ? { matchWithPreviousDesigner } : {}),
    });
}

// Shares mutation input mapping across booking paths.
export function buildDesignLiveAppointmentInput(
    appointmentTime: string,
    timezone: string,
    calendarId?: number
): DesignLiveAppointmentUpdatedEventInput {
    return { appointmentTime, timezone, acuityCalendarId: calendarId };
}
